import type { z } from 'zod';

// A dependency-free leaf module, so reaching into the feature folder from here
// introduces no cycle. It lives there because that is where it is understood -
// which store the storefront is serving is a storefront concern, and this file
// only needs the answer to renew a shopper's session.
import { followStoreMove, resolveStorefrontHost } from '@/features/storefront/storefrontHost';
// A leaf too: a refusal for a lapsed plan is noticed here, once, and the auth
// provider re-reads the lock so the route guard can act on it.
import { reportBillingLock } from '@/features/billing/lib/billingLock';
// Also a leaf, for the same reason: a refusal for maintenance is noticed here,
// once, rather than by every page that happens to make the refused request.
import { maintenanceFromDetails, reportMaintenanceRefusal } from '@/features/system/maintenance';
import { reportFeatureDisabled } from '@/features/system/pausedFlags';

import { createSseParser } from './sse';
import { customerTokenStore, tokenStore, type TokenStore } from './tokenStore';
import { errorResponseSchema, successResponseSchema } from './types';

function storefrontHostHeader(): Record<string, string> {
  const host = resolveStorefrontHost();
  return host ? { 'X-Storefront-Host': host } : {};
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** A failed request, carrying the backend's stable error code. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | null;

  constructor(
    message: string,
    options: { code: string; status: number; details?: Record<string, unknown> | null },
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details ?? null;
  }
}

/**
 * Which population this request authenticates as.
 *
 * Two entirely separate sessions can be live in one browser: a merchant signed
 * in to the dashboard, and a shopper signed in to a storefront - which is
 * exactly what happens when a merchant previews their own shop. They hold
 * different tokens, refresh at different endpoints and must never be confused
 * for one another, so the realm is named at every call site rather than
 * inferred from the path.
 *
 * `none` is for the public storefront - product listings, the cart - which
 * should not carry a token at all.
 */
export type AuthRealm = 'merchant' | 'customer' | 'none';

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Prefix the path with the versioned API base (default) or hit the root. */
  baseUrl?: string;
  /** Skip the automatic refresh-and-retry on 401 (used by auth calls). */
  skipAuthRefresh?: boolean;
  /** Whose session to send. Defaults to the merchant dashboard's. */
  auth?: AuthRealm;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError('The server returned a malformed response.', {
      code: 'INVALID_RESPONSE',
      status: response.status,
    });
  }
}

/**
 * A request refused because the platform is in a maintenance window: the
 * layouts swap to the maintenance page (full) or the toast explains why the
 * save did not happen (read-only). The error still reaches the caller.
 */
function noteMaintenance(code: string, details: Record<string, unknown> | null, message: string) {
  if (code !== 'MAINTENANCE') return;
  reportMaintenanceRefusal(maintenanceFromDetails(details, message));
}

function toApiError(payload: unknown, status: number): ApiError {
  const parsed = errorResponseSchema.safeParse(payload);
  if (parsed.success) {
    // A shop that renamed its subdomain: the storefront moves to the new
    // address, and the error still reaches the caller for anything else.
    if (parsed.data.error.code === 'STORE_MOVED') followStoreMove(parsed.data.error.details ?? null);
    noteMaintenance(parsed.data.error.code, parsed.data.error.details ?? null, parsed.data.error.message);
    // The merchant's plan lapsed and the store was locked while this tab was
    // open. Only this code: `PLAN_LIMIT_REACHED` is also a 402, and is a
    // page's own business to explain.
    if (status === 402 && parsed.data.error.code === 'BILLING_LOCKED') reportBillingLock();
    // A kill switch flipped while this tab was open: the dashboard asks which
    // capabilities are paused, so the page can say so rather than just fail.
    if (status === 503 && parsed.data.error.code === 'FEATURE_DISABLED') {
      reportFeatureDisabled(parsed.data.error.details ?? null);
    }
    return new ApiError(parsed.data.error.message, {
      code: parsed.data.error.code,
      status,
      details: parsed.data.error.details ?? null,
    });
  }
  return new ApiError(`Request failed with status ${status}.`, { code: 'HTTP_ERROR', status });
}

/**
 * How each realm holds a token and where it renews one.
 *
 * `extraHeaders` exists for the storefront: its refresh endpoint is
 * hostname-resolved like every other public storefront call, so the request
 * has to say which store it is asking about before it can be told whose
 * session this is.
 */
interface RealmAdapter {
  store: TokenStore;
  refreshPath: string;
  extraHeaders?: () => Record<string, string>;
}

const REALMS: Record<Exclude<AuthRealm, 'none'>, RealmAdapter> = {
  merchant: { store: tokenStore, refreshPath: '/auth/refresh' },
  customer: {
    store: customerTokenStore,
    refreshPath: '/storefront/account/refresh',
    extraHeaders: storefrontHostHeader,
  },
};

/**
 * Refresh in flight, per realm, shared by every concurrent 401.
 *
 * Without this, a page that fires five queries on mount would send five
 * refreshes; since refresh tokens rotate, four of them would present an
 * already-rotated token and the backend would (correctly) treat that as theft
 * and revoke the session. Keyed by realm because a storefront page can have a
 * shopper session renewing at the same moment as a merchant one.
 */
const refreshInFlight = new Map<AuthRealm, Promise<string | null>>();

async function requestNewAccessToken(realm: RealmAdapter): Promise<string | null> {
  const response = await fetch(`${API_BASE_URL}${realm.refreshPath}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', ...(realm.extraHeaders?.() ?? {}) },
  });

  if (!response.ok) {
    realm.store.clear();
    return null;
  }

  const payload = (await response.json()) as { data?: { access_token?: string } };
  const token = payload.data?.access_token ?? null;
  realm.store.set(token);
  return token;
}

export function refreshSession(realm: AuthRealm = 'merchant'): Promise<string | null> {
  if (realm === 'none') return Promise.resolve(null);

  const pending = refreshInFlight.get(realm);
  if (pending) return pending;

  const started = requestNewAccessToken(REALMS[realm]).finally(() => {
    refreshInFlight.delete(realm);
  });
  refreshInFlight.set(realm, started);
  return started;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const {
    body,
    baseUrl = API_BASE_URL,
    headers,
    skipAuthRefresh,
    auth = 'merchant',
    ...init
  } = options;
  const realm = auth === 'none' ? null : REALMS[auth];
  const token = realm?.store.get() ?? null;

  // File uploads go up as multipart. The browser has to set that Content-Type
  // itself, because only it knows the boundary it generated.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const doFetch = (authorization: string | null) =>
    fetch(`${baseUrl}${path}`, {
      ...init,
      // Public storefront reads a CDN may cache go without cookies; see
      // `publicRead` in features/storefront/api/storefront.ts.
      credentials: init.credentials ?? 'include',
      headers: {
        Accept: 'application/json',
        ...(body === undefined || isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(authorization ? { Authorization: `Bearer ${authorization}` } : {}),
        ...headers,
      },
      ...(body === undefined ? {} : { body: isFormData ? body : JSON.stringify(body) }),
    });

  const response = await doFetch(token);

  if (response.status !== 401 || skipAuthRefresh || !realm || !realm.store.isRefreshable()) {
    return response;
  }

  const refreshed = await refreshSession(auth);
  return refreshed ? doFetch(refreshed) : response;
}

/**
 * Perform a request and return the *unwrapped* payload.
 *
 * `schema` describes the contents of `data`, not the envelope: the envelope is
 * peeled off here so callers never handle `{ success, data }` themselves.
 */
export async function apiRequest<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const response = await send(path, options);
  const payload = await readJson(response);

  if (!response.ok) {
    throw toApiError(payload, response.status);
  }

  const envelope = successResponseSchema(schema).safeParse(payload);
  if (!envelope.success) {
    throw new ApiError('The server response did not match the expected shape.', {
      code: 'INVALID_RESPONSE',
      status: response.status,
    });
  }

  return envelope.data.data as z.infer<TSchema>;
}

export interface StreamOptions extends RequestOptions {
  /** Every event but `done` and `error`, with its data already decoded. */
  onEvent?: (event: string, data: unknown) => void;
}

/**
 * Perform a request answered with Server-Sent Events, and return the `done` payload.
 *
 * Refusals arrive before the stream as the usual JSON error and throw
 * `ApiError`, exactly as `apiRequest` would; an `error` event mid-stream throws
 * `ApiError` with its code and status. Progress events go to `onEvent`. A
 * response that is plain JSON after all (a proxy that does not stream, an older
 * server) resolves with its `data`, so a caller never has to tell the two apart.
 *
 * Pass `signal` to stop: the read is abandoned and the `AbortError` rethrown.
 */
export async function apiStream<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: StreamOptions = {},
): Promise<z.infer<TSchema>> {
  const { onEvent, headers, ...rest } = options;
  const response = await send(path, {
    ...rest,
    headers: { Accept: 'text/event-stream', ...headers },
  });

  if (!response.ok) {
    throw toApiError(await readJson(response), response.status);
  }

  const invalid = () =>
    new ApiError('The server response did not match the expected shape.', {
      code: 'INVALID_RESPONSE',
      status: response.status,
    });

  if (!(response.headers.get('content-type') ?? '').includes('text/event-stream') || !response.body) {
    const envelope = successResponseSchema(schema).safeParse(await readJson(response));
    if (!envelope.success) throw invalid();
    return envelope.data.data as z.infer<TSchema>;
  }

  let outcome: { done: z.infer<TSchema> } | { failure: ApiError } | null = null;
  const parser = createSseParser(({ event, data }) => {
    if (outcome) return;
    let decoded: unknown;
    try {
      decoded = JSON.parse(data) as unknown;
    } catch {
      outcome = { failure: invalid() };
      return;
    }
    if (event === 'done') {
      const parsed = schema.safeParse(decoded);
      outcome = parsed.success ? { done: parsed.data as z.infer<TSchema> } : { failure: invalid() };
    } else if (event === 'error') {
      const error = (decoded ?? {}) as { code?: string; message?: string; status?: number; details?: Record<string, unknown> | null };
      noteMaintenance(error.code ?? '', error.details ?? null, error.message ?? '');
      outcome = {
        failure: new ApiError(error.message ?? 'The AI could not finish.', {
          code: error.code ?? 'HTTP_ERROR',
          status: error.status ?? 503,
          details: error.details ?? null,
        }),
      };
    } else {
      onEvent?.(event, decoded);
    }
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  // Stopping must end the read even where the body does not watch the signal.
  const cancelRead = () => void reader.cancel().catch(() => undefined);
  rest.signal?.addEventListener('abort', cancelRead, { once: true });
  try {
    while (!outcome) {
      const { value, done } = await reader.read();
      if (done) {
        parser.push(decoder.decode());
        parser.end();
        break;
      }
      parser.push(decoder.decode(value, { stream: true }));
    }
  } finally {
    rest.signal?.removeEventListener('abort', cancelRead);
    if (outcome) void reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
  if (rest.signal?.aborted) throw new DOMException('The request was stopped.', 'AbortError');

  // Assigned inside the parser callback, which TypeScript cannot follow.
  const settled = outcome as { done: z.infer<TSchema> } | { failure: ApiError } | null;
  if (!settled) {
    throw new ApiError('The connection closed before the AI finished.', {
      code: 'STREAM_INTERRUPTED',
      status: 0,
    });
  }
  if ('failure' in settled) throw settled.failure;
  return settled.done;
}

/**
 * Fetch a binary payload - currently invoice PDFs.
 *
 * These endpoints authenticate like any other, so they go through `send` to
 * pick up the bearer token and the refresh-and-retry; only the decoding of a
 * successful response differs. Failures still arrive as the JSON envelope.
 */
export async function apiRequestBlob(
  path: string,
  options: RequestOptions = {},
  onProgress?: (progress: DownloadProgress) => void,
): Promise<Blob> {
  const response = await send(path, options);
  if (!response.ok) {
    throw toApiError(await readJson(response), response.status);
  }
  if (!onProgress || !response.body) return response.blob();

  // Read it chunk by chunk so a long download can say how far along it is.
  // The total is the server's Content-Length; a compressed response counts
  // more bytes than that, and is then reported without one.
  const length = Number(response.headers.get('Content-Length'));
  const total = Number.isFinite(length) && length > 0 ? length : null;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  onProgress({ received, total });
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    onProgress({ received, total: total !== null && received <= total ? total : null });
  }
  return new Blob(chunks as BlobPart[], { type: response.headers.get('Content-Type') ?? undefined });
}

/** How much of a download has arrived; `total` is null when the server did not say. */
export interface DownloadProgress {
  received: number;
  total: number | null;
}

/** How much of an upload has left the browser, as a fraction of the whole body. */
export interface UploadProgress {
  sent: number;
  total: number;
}

function formHasFiles(form: FormData): boolean {
  for (const value of form.values()) if (value instanceof File) return true;
  return false;
}

/**
 * Send a multipart form and report how much of it has gone, for the few
 * places a person watches a file leave: `fetch` cannot say, so a body that
 * carries a file goes over `XMLHttpRequest`. One without files takes the
 * ordinary path, which is also what the test harness stubs.
 *
 * Authenticates like `apiRequest`, including one refresh-and-retry on a 401,
 * and decodes the same envelope; a failure arrives as the same `ApiError`.
 */
export async function apiUpload<TSchema extends z.ZodTypeAny>(
  path: string,
  form: FormData,
  schema: TSchema,
  options: { onProgress?: (progress: UploadProgress) => void; auth?: AuthRealm } = {},
): Promise<z.infer<TSchema>> {
  const { onProgress, auth = 'merchant' } = options;
  if (!onProgress || !formHasFiles(form) || typeof XMLHttpRequest === 'undefined') {
    return apiRequest(path, schema, { method: 'POST', body: form, auth });
  }
  const realm = auth === 'none' ? null : REALMS[auth];

  const attempt = (authorization: string | null) =>
    new Promise<{ status: number; payload: unknown }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}${path}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      if (authorization) xhr.setRequestHeader('Authorization', `Bearer ${authorization}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress({ sent: event.loaded, total: event.total });
      };
      xhr.onerror = () => reject(new ApiError('The upload could not be sent.', { code: 'NETWORK_ERROR', status: 0 }));
      xhr.onload = () => {
        let payload: unknown = null;
        if (xhr.responseText) {
          try {
            payload = JSON.parse(xhr.responseText);
          } catch {
            payload = null;
          }
        }
        resolve({ status: xhr.status, payload });
      };
      xhr.send(form);
    });

  let result = await attempt(realm?.store.get() ?? null);
  if (result.status === 401 && realm?.store.isRefreshable()) {
    const refreshed = await refreshSession(auth);
    if (refreshed) result = await attempt(refreshed);
  }
  if (result.status < 200 || result.status >= 300) {
    throw toApiError(result.payload, result.status);
  }
  const envelope = successResponseSchema(schema).safeParse(result.payload);
  if (!envelope.success) {
    throw new ApiError('The server response did not match the expected shape.', {
      code: 'INVALID_RESPONSE',
      status: result.status,
    });
  }
  return envelope.data.data as z.infer<TSchema>;
}

/**
 * Request an endpoint that returns a bare object rather than the envelope -
 * currently only the infrastructure probes (`/health`, `/ready`).
 */
export async function apiRequestRaw<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const response = await fetch(`${options.baseUrl ?? ''}${path}`, {
    headers: { Accept: 'application/json' },
    ...options,
    body: undefined,
  });

  const payload = await readJson(response);
  const parsed = schema.safeParse(payload);

  if (!parsed.success) {
    throw new ApiError('The server response did not match the expected shape.', {
      code: 'INVALID_RESPONSE',
      status: response.status,
    });
  }

  return parsed.data as z.infer<TSchema>;
}
